import { AxiosError } from 'axios'
import { ApiProvider } from '../provider'
import { APIRoutes } from '../routes'
import {
  BrokerDetails,
  BrokerUsers,
  GetBrokerContactresponseBody,
  GetBrokerListResponseBody,
  GetStatementListResponseBody,
  GetStatementResponseBody,
} from 'api/types/broker'

class Broker {
  // get all brokers a user belongs to
  async getBrokerContact({
    brokerNo,
  }: {
    brokerNo: number
  }): Promise<GetBrokerContactresponseBody | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.getBrokerContact}?brokerNo=${brokerNo}&allBrokersForContact=true`
    return ApiProvider.getRequest(url) as Promise<
      GetBrokerContactresponseBody | (AxiosError | Error)
    >
  }

  // get broker statement list
  async getBrokerStatementList({
    brokerNo,
    yearMonth,
  }: {
    brokerNo: number
    yearMonth: string
  }): Promise<GetStatementListResponseBody | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.getBrokerStatementList}?brokerNo=${brokerNo}&yearMonth=${yearMonth}`
    return ApiProvider.getRequest(url) as Promise<
      GetStatementListResponseBody | (AxiosError | Error)
    >
  }

  // get broker statement
  async getBrokerStatement({
    brokerNo,
    statementId,
  }: {
    brokerNo: number
    statementId: string
  }): Promise<GetStatementResponseBody | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.getBrokerStatement}?brokerNo=${brokerNo}&statementId=${statementId}`
    return ApiProvider.getRequest(url) as Promise<
      GetStatementResponseBody | (AxiosError | Error)
    >
  }

  // get broker details
  async getBroker({
    brokerNo,
  }: {
    brokerNo: number
  }): Promise<BrokerDetails | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.getBroker}?id=${brokerNo}`
    return ApiProvider.getRequest(url) as Promise<
      BrokerDetails | (AxiosError | Error)
    >
  }

  // get broker users
  async getBrokerContacts({
    brokerNo,
  }: {
    brokerNo: number
  }): Promise<BrokerUsers | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.getBrokerContacts}?brokerNo=${brokerNo}`
    return ApiProvider.getRequest(url) as Promise<
      BrokerUsers | (AxiosError | Error)
    >
  }

  async changeBrokerContactAccessLevel({
    brokerNo,
    contactNo,
    accessLevel,
  }: {
    brokerNo: number
    contactNo: number
    accessLevel: number
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.changeBrokerContactAccessLevel}?brokerNo=${brokerNo}&contactNo=${contactNo}&accesslevel=${accessLevel}`
    return ApiProvider.postRequest(url, {}, false) as Promise<
      any | (AxiosError | Error)
    >
  }

  async getBrokerList(): Promise<
    GetBrokerListResponseBody | (AxiosError | Error)
  > {
    const url = `${APIRoutes.broker.getBrokerList}`
    return ApiProvider.getRequest(url) as Promise<
      GetBrokerListResponseBody | (AxiosError | Error)
    >
  }

  async enableBrokerContact({
    brokerNo,
    contactNo,
  }: {
    brokerNo: number
    contactNo: number
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.enableBrokerContact}?brokerNo=${brokerNo}&contactNo=${contactNo}`
    return ApiProvider.postRequest(url, {}, false) as Promise<
      any | (AxiosError | Error)
    >
  }

  async disableBrokerContact({
    brokerNo,
    contactNo,
  }: {
    brokerNo: number
    contactNo: number
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.disableBrokerContact}?brokerNo=${brokerNo}&contactNo=${contactNo}`
    return ApiProvider.postRequest(url, {}, false) as Promise<
      any | (AxiosError | Error)
    >
  }

  async saveBrokerReq({
    company,
    nif,
    mobile,
    adminName,
    email,
  }: {
    company: string
    nif: string
    mobile: string
    adminName: string
    email: string
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.saveBrokerReq}`

    const formData = new FormData()
    formData.append('company', company)
    formData.append('nif', nif)
    formData.append('mobile', mobile)
    formData.append('adminName', adminName)
    formData.append('email', email)

    return ApiProvider.postRequest(url, formData, true) as Promise<
      any | (AxiosError | Error)
    >
  }

  async getBrokerReqList(): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.getBrokerReqList}`
    return ApiProvider.getRequest(url) as Promise<any | (AxiosError | Error)>
  }

  async deleteBrokerReq({
    id,
  }: {
    id: number
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.deleteBrokerReq}?Id=${id}`
    return ApiProvider.deleteRequest(url, {}) as Promise<
      any | (AxiosError | Error)
    >
  }

  async addBrokerReqFiles({
    brokerRequestID,
    files,
  }: {
    brokerRequestID: number
    files: File[]
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.broker.addBrokerReqFiles}`

    const formData = new FormData()
    formData.append('Id', brokerRequestID.toString())
    files.forEach((file, index) => {
      const labelFieldKey = `Files[${index}].label`
      const fileFieldKey = `Files[${index}].File`

      let label

      switch (index) {
        case 0:
          label = 'Ficha Nomeação'
          break
        case 1:
          label = 'Apólice Responsabilidade Civil'
          break
        case 2:
          label = 'Relatório e Contas'
          break
        case 3:
          label = 'Certificado ASF de Mediador'
          break
        case 4:
          label = 'IBAN'
          break
        default:
          label = `Documento ${index + 1}`
      }
      formData.append(labelFieldKey, label)
      formData.append(fileFieldKey, file)
    })

    return ApiProvider.postRequest(url, formData, true) as Promise<
      any | (AxiosError | Error)
    >
  }
}

export const brokerService = new Broker()
